import { generatePath, Navigate, useLocation, useParams } from 'react-router-dom';

import { mergeRedirectSearch } from './merge-redirect-search';

import type { ReactElement } from 'react';

/**
 * A `<Navigate>` that carries the current query string — and any matched path
 * params — to the new path. Used for renamed or merged routes whose bookmarks
 * may pin something worth keeping (`?tab=segments`, `?nudge=<id>`).
 *
 * Three failures it exists to prevent:
 *
 *  - A bare `<Navigate to="/x">` DROPS the search, so a bookmark pinning a tab
 *    (`/user-learning?tab=settings`) lands on the default tab instead.
 *  - A `to` naming a param (`/system-logs/event/:eventId`) is navigated to
 *    LITERALLY by `<Navigate>` — a reader following an audit-event permalink
 *    would arrive at a page looking for an event whose id is the string
 *    ":eventId". `generatePath` fills params from the current match, so a
 *    permalink issued under the old path keeps opening the same event.
 *  - Concatenating the two queries (`${to}${search}`) emits a second `?`, which
 *    the router reads as ONE param whose value is the whole rest of the string
 *    — `?tab=placement?tab=all`, which fails the destination's parse and lands
 *    on its default tab, silently, on a URL somebody saved.
 *
 * The query fold is {@link mergeRedirectSearch} — destination-wins, so a
 * destination that names its own tab still gets one, while keys it does not
 * name keep every value the bookmark had, repeats included. It lives in its own
 * leaf module because it is a pure function and this file exports a component;
 * having it there rather than inline is also what keeps ONE implementation of
 * the fold, which two branches independently wrote and which must not drift.
 *
 * The split below exists because `generatePath` understands a PATH pattern and
 * nothing else — handing it a `?query` or a `#hash` would make them part of the
 * pattern. So the path is filled first and the parts are handed back to
 * `mergeRedirectSearch`, which re-splits them and owns the query/hash rules.
 */
export function RedirectPreservingSearch({ to }: { to: string }): ReactElement {
  const { search } = useLocation();
  const params = useParams();

  const hashAt = to.indexOf('#');
  const hash = hashAt === -1 ? '' : to.slice(hashAt);
  const withoutHash = hashAt === -1 ? to : to.slice(0, hashAt);
  const queryAt = withoutHash.indexOf('?');
  const rawPath = queryAt === -1 ? withoutHash : withoutHash.slice(0, queryAt);
  const rawQuery = queryAt === -1 ? '' : withoutHash.slice(queryAt);

  // A `to` with no `:param` passes through `generatePath` unchanged, so the
  // common case needs no branch — and only the CURRENT match's params are
  // offered, so a `to` naming one the source route does not capture throws here
  // rather than silently producing a broken URL.
  const filled = `${generatePath(rawPath, params)}${rawQuery}${hash}`;

  return <Navigate to={mergeRedirectSearch(filled, search)} replace />;
}
